import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  convertPublishedCatalogImage,
  deleteMerchantIcon,
  fetchCatalogSnapshot,
  saveMerchantIcon,
  uploadCatalogImage,
} from "../lib/catalogApi";
import { isVectorAssetURL } from "../lib/catalogImages";
import type { Institution, MerchantIcon, MerchantSourceKind, WalletCategory } from "../lib/catalogTypes";
import {
  MERCHANT_SOURCE_KINDS,
  WALLET_CATEGORIES,
  countryScopeIsGlobal,
  encodeCountryScope,
  parseCountryScopeJSON,
  parseMatchTermsJSON,
  walletCategoryFromHint,
  walletCategoryLabel,
} from "../lib/catalogTypes";
import { OSM_TAG_MAPPINGS } from "../lib/merchantCategoryMap";
import {
  CURATED_MERCHANT_ICONS,
  SIMPLE_ICONS_CDN_VERSION,
  simpleIconsSvgUrl,
  type CuratedMerchantIcon,
} from "../lib/simpleIconsCurated";
import {
  catalogEntryToImportDraft,
  filterSimpleIconsCatalog,
  loadSimpleIconsCatalog,
  simpleIconsPreviewUrl,
  type SimpleIconsCatalogEntry,
} from "../lib/simpleIconsCatalog";
import { fetchSimpleIconFile } from "../lib/simpleIconsImport";
import { MerchantBrandImportPanel } from "./MerchantBrandImportPanel";

type Draft = Partial<MerchantIcon> & { matchTermsText?: string };

type ScopeFilter = "all" | "ph" | "global";
type ReviewFilter = "all" | "needsReview" | "merged";
type IconFilter = "all" | "hasIcon" | "missingIcon";
type ViewMode = "gallery" | "list";

function countriesLabel(row: MerchantIcon): string {
  const codes = parseCountryScopeJSON(row.countryScopeJSON);
  if (codes.includes("001")) {
    return codes.length === 1
      ? "Global"
      : `Global · ${codes.filter((c) => c !== "001").join(", ").toUpperCase()}`;
  }
  if (codes.includes("ph")) return "PH";
  return codes.length ? codes.map((c) => c.toUpperCase()).join(", ") : "—";
}

function termsFromJson(json: string | null | undefined): string {
  return parseMatchTermsJSON(json).join(", ");
}

function parseTerms(text: string | undefined): string[] {
  if (!text?.trim()) return [];
  return text
    .split(/[,;\n]/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

function defaultDraft(): Draft {
  return {
    displayName: "",
    normalizedName: "",
    sourceSlug: "",
    sourceName: "cms",
    sourceKind: "cms",
    matchTermsText: "",
    brandHex: "000000",
    walletCategory: "other",
    granularKind: "cms/other",
    categoryHint: "Other",
    countryScopeJSON: encodeCountryScope(["ph"]),
    wikidataId: null,
    nsiId: null,
    mergedIntoRecordName: null,
    sortOrder: 0,
    isActive: true,
    iconURL: null,
  };
}

export function MerchantIconsPage() {
  const [rows, setRows] = useState<MerchantIcon[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [walletFilter, setWalletFilter] = useState<WalletCategory | "all">("all");
  const [kindFilter, setKindFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<MerchantSourceKind | "all">("all");
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("all");
  const [iconFilter, setIconFilter] = useState<IconFilter>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("gallery");
  const [importBusySlug, setImportBusySlug] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SimpleIconsCatalogEntry[] | null>(null);
  const [catalogQuery, setCatalogQuery] = useState("");
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(() => new Set());
  const [showCachedInCatalog, setShowCachedInCatalog] = useState(false);
  const [uploadAsIs, setUploadAsIs] = useState(false);

  const reload = useCallback(async () => {
    const snap = await fetchCatalogSnapshot();
    setRows(snap.merchantIcons);
    setInstitutions(snap.institutions);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kindQ = kindFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (walletFilter !== "all" && r.walletCategory !== walletFilter) return false;
      if (sourceFilter !== "all" && r.sourceKind !== sourceFilter) return false;
      if (kindQ && !r.granularKind.toLowerCase().includes(kindQ)) return false;
      const countries = parseCountryScopeJSON(r.countryScopeJSON);
      if (scopeFilter === "ph" && !countries.includes("ph")) return false;
      if (scopeFilter === "global" && !countryScopeIsGlobal(countries)) return false;
      if (reviewFilter === "merged" && !r.mergedIntoRecordName) return false;
      if (reviewFilter === "needsReview") {
        const needs =
          !r.isActive ||
          Boolean(r.mergedIntoRecordName) ||
          r.walletCategory === "other" ||
          !r.granularKind;
        if (!needs) return false;
      }
      if (iconFilter === "hasIcon" && !r.iconURL) return false;
      if (iconFilter === "missingIcon" && Boolean(r.iconURL)) return false;
      if (!q) return true;
      return (
        r.displayName.toLowerCase().includes(q) ||
        r.normalizedName.includes(q) ||
        r.sourceSlug.includes(q) ||
        r.granularKind.toLowerCase().includes(q) ||
        r.walletCategory.toLowerCase().includes(q) ||
        (r.wikidataId ?? "").toLowerCase().includes(q) ||
        (r.nsiId ?? "").toLowerCase().includes(q) ||
        (r.categoryHint ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, query, walletFilter, kindFilter, scopeFilter, sourceFilter, reviewFilter, iconFilter]);

  const missingIconCount = useMemo(
    () => rows.filter((row) => !row.iconURL).length,
    [rows],
  );
  const withIconCount = rows.length - missingIconCount;

  const cachedSlugs = useMemo(() => new Set(rows.map((r) => r.sourceSlug)), [rows]);

  const catalogMatches = useMemo(() => {
    if (!catalog) return [];
    const matches = filterSimpleIconsCatalog(catalog, catalogQuery, showCachedInCatalog ? 160 : 200);
    if (showCachedInCatalog) return matches;
    return matches.filter((entry) => !cachedSlugs.has(entry.slug));
  }, [catalog, catalogQuery, cachedSlugs, showCachedInCatalog]);

  const selectedNotCached = useMemo(
    () => [...selectedSlugs].filter((slug) => !cachedSlugs.has(slug)),
    [selectedSlugs, cachedSlugs],
  );

  async function ensureCatalogLoaded(force = false) {
    if (catalog && !force) return catalog;
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const entries = await loadSimpleIconsCatalog(force);
      setCatalog(entries);
      return entries;
    } catch (err) {
      setCatalogError(err instanceof Error ? err.message : "Could not load Simple Icons catalog.");
      throw err;
    } finally {
      setCatalogLoading(false);
    }
  }

  function toggleCatalogSlug(slug: string) {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  function selectVisibleCatalog() {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      for (const entry of catalogMatches) {
        if (!cachedSlugs.has(entry.slug)) next.add(entry.slug);
      }
      return next;
    });
  }

  function clearCatalogSelection() {
    setSelectedSlugs(new Set());
  }

  async function importCatalogSelection() {
    const entries = catalog ?? (await ensureCatalogLoaded());
    const bySlug = new Map(entries.map((entry) => [entry.slug, entry]));
    const queue = selectedNotCached
      .map((slug) => bySlug.get(slug))
      .filter((entry): entry is SimpleIconsCatalogEntry => Boolean(entry));
    if (queue.length === 0) {
      setMessage("Choose one or more brands that are not already cached.");
      return;
    }
    if (!window.confirm(`Import and cache ${queue.length} icon(s) to R2?`)) return;

    setBusy(true);
    setError(null);
    setMessage(null);
    let ok = 0;
    const failures: string[] = [];
    for (const entry of queue) {
      try {
        await importCurated(catalogEntryToImportDraft(entry), { silent: true });
        ok += 1;
        setSelectedSlugs((prev) => {
          const next = new Set(prev);
          next.delete(entry.slug);
          return next;
        });
      } catch {
        failures.push(entry.title);
      }
    }
    await reload();
    setBusy(false);
    setMessage(
      failures.length
        ? `Imported ${ok}. Failed: ${failures.join(", ")}`
        : `Imported ${ok} icon(s) from the Simple Icons catalog.`,
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.displayName || !editing.normalizedName || !editing.sourceSlug) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await saveMerchantIcon({
        recordName: editing.recordName,
        displayName: editing.displayName,
        normalizedName: editing.normalizedName,
        matchTerms: parseTerms(editing.matchTermsText),
        sourceSlug: editing.sourceSlug,
        sourceName: editing.sourceName || "cms",
        brandHex: editing.brandHex ?? null,
        iconURL: editing.iconURL ?? null,
        categoryHint: walletCategoryLabel(
          editing.walletCategory ?? walletCategoryFromHint(editing.categoryHint),
        ),
        walletCategory: editing.walletCategory ?? walletCategoryFromHint(editing.categoryHint),
        granularKind: editing.granularKind ?? undefined,
        countryScopeJSON: editing.countryScopeJSON ?? null,
        wikidataId: editing.wikidataId ?? null,
        nsiId: editing.nsiId ?? null,
        sourceKind: editing.sourceKind ?? "cms",
        mergedIntoRecordName: editing.mergedIntoRecordName ?? null,
        isActive: editing.isActive !== false,
        sortOrder: Number(editing.sortOrder ?? 0),
        matchTermsJSON: editing.matchTermsJSON ?? "[]",
      });
      setEditing(null);
      setMessage("Merchant saved.");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onUploadReplacement(file: File | null) {
    if (!editing || !file) return;
    setBusy(true);
    setError(null);
    try {
      const url = await uploadCatalogImage(file, "merchant", {
        merchantPreserveColors: uploadAsIs,
      });
      setEditing({ ...editing, iconURL: url });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function importCurated(
    item: Pick<
      CuratedMerchantIcon,
      "sourceSlug" | "displayName" | "normalizedName" | "matchTerms" | "brandHex" | "categoryHint" | "sortOrder"
    >,
    opts?: { silent?: boolean },
  ) {
    setImportBusySlug(item.sourceSlug);
    if (!opts?.silent) {
      setError(null);
      setMessage(null);
    }
    try {
      const file = await fetchSimpleIconFile(simpleIconsSvgUrl(item.sourceSlug));
      const iconURL = await uploadCatalogImage(file, "merchant");
      await saveMerchantIcon({
        displayName: item.displayName,
        normalizedName: item.normalizedName,
        matchTerms: item.matchTerms,
        sourceSlug: item.sourceSlug,
        sourceName: "simple-icons",
        brandHex: item.brandHex,
        iconURL,
        categoryHint: item.categoryHint,
        walletCategory: walletCategoryFromHint(item.categoryHint),
        sourceKind: "simple-icons",
        countryScope: ["001"],
        isActive: true,
        sortOrder: item.sortOrder,
        matchTermsJSON: JSON.stringify(item.matchTerms),
      });
      if (!opts?.silent) {
        setMessage(`Imported ${item.displayName} → R2 PNG cached.`);
        await reload();
      }
    } catch (err) {
      if (!opts?.silent) {
        setError(err instanceof Error ? err.message : `Import failed for ${item.displayName}`);
      }
      throw err;
    } finally {
      setImportBusySlug(null);
    }
  }

  async function redownload(row: MerchantIcon, opts?: { silent?: boolean }) {
    setImportBusySlug(row.sourceSlug);
    if (!opts?.silent) {
      setError(null);
      setMessage(null);
    }
    try {
      const file = await fetchSimpleIconFile(simpleIconsSvgUrl(row.sourceSlug));
      const iconURL = await uploadCatalogImage(file, "merchant");
      await saveMerchantIcon({
        ...row,
        recordName: row.recordName,
        iconURL,
        matchTerms: parseTerms(termsFromJson(row.matchTermsJSON)),
      });
      if (!opts?.silent) {
        setMessage(`Redownloaded ${row.displayName} → R2 PNG replaced.`);
        await reload();
      }
    } catch (err) {
      if (!opts?.silent) {
        setError(err instanceof Error ? err.message : `Redownload failed for ${row.displayName}`);
      }
      throw err;
    } finally {
      setImportBusySlug(null);
    }
  }

  /**
   * Re-publishes a legacy SVG icon as PNG. Redownload only covers Simple Icons records,
   * so a hand-uploaded vector needs this path instead.
   */
  async function convertToPng(row: MerchantIcon, opts?: { silent?: boolean }) {
    if (!row.iconURL || !isVectorAssetURL(row.iconURL)) return;
    setImportBusySlug(row.sourceSlug);
    if (!opts?.silent) {
      setError(null);
      setMessage(null);
    }
    try {
      const iconURL = await convertPublishedCatalogImage(row.iconURL, "merchant");
      await saveMerchantIcon({
        ...row,
        recordName: row.recordName,
        iconURL,
        matchTerms: parseTerms(termsFromJson(row.matchTermsJSON)),
      });
      if (!opts?.silent) {
        setMessage(`Converted ${row.displayName} to an app-ready PNG.`);
        await reload();
      }
    } catch (err) {
      if (!opts?.silent) {
        setError(err instanceof Error ? err.message : `Conversion failed for ${row.displayName}`);
      }
      throw err;
    } finally {
      setImportBusySlug(null);
    }
  }

  async function convertAllToPng() {
    const vectors = rows.filter((row) => isVectorAssetURL(row.iconURL));
    if (vectors.length === 0) {
      setMessage("No merchant SVG files need conversion.");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    let ok = 0;
    const failures: string[] = [];
    for (const row of vectors) {
      try {
        await convertToPng(row, { silent: true });
        ok += 1;
      } catch {
        failures.push(row.displayName);
      }
    }
    await reload();
    setBusy(false);
    setMessage(
      failures.length
        ? `Converted ${ok} to PNG. Failed: ${failures.join(", ")}`
        : `Converted ${ok} merchant icon(s) to PNG.`,
    );
  }

  async function redownloadAll() {
    const downloadable = rows.filter((row) => row.sourceName === "simple-icons");
    if (downloadable.length === 0) {
      setMessage("No Simple Icons merchant records are available to redownload.");
      return;
    }
    if (!window.confirm(`Redownload and replace ${downloadable.length} merchant icon(s)?`)) return;

    setBusy(true);
    setError(null);
    setMessage(null);
    let ok = 0;
    const failures: string[] = [];
    for (const row of downloadable) {
      try {
        await redownload(row, { silent: true });
        ok += 1;
      } catch {
        failures.push(row.displayName);
      }
    }
    await reload();
    setBusy(false);
    setMessage(
      failures.length
        ? `Redownloaded ${ok}. Failed: ${failures.join(", ")}`
        : `Redownloaded ${ok} merchant icon(s).`,
    );
  }

  async function importMissing() {
    const missing = CURATED_MERCHANT_ICONS.filter((c) => !cachedSlugs.has(c.sourceSlug));
    if (missing.length === 0) {
      setMessage("All curated brands are already cached.");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    let ok = 0;
    const failures: string[] = [];
    for (const item of missing) {
      try {
        await importCurated(item, { silent: true });
        ok += 1;
      } catch {
        failures.push(item.displayName);
      }
    }
    await reload();
    setBusy(false);
    setMessage(
      failures.length
        ? `Imported ${ok}. Failed: ${failures.join(", ")}`
        : `Imported ${ok} merchant icon(s).`,
    );
  }

  async function onDeactivate(row: MerchantIcon) {
    if (!window.confirm(`Deactivate ${row.displayName}? The CloudKit record name is kept.`)) return;
    setBusy(true);
    setError(null);
    try {
      await saveMerchantIcon({
        ...row,
        recordName: row.recordName,
        matchTerms: parseTerms(termsFromJson(row.matchTermsJSON)),
        isActive: false,
      });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Deactivate failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(row: MerchantIcon) {
    if (!window.confirm(`Permanently delete ${row.displayName}? Prefer Deactivate to keep the record name.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMerchantIcon(row.recordName);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(row: MerchantIcon) {
    setEditing({
      ...row,
      matchTermsText: termsFromJson(row.matchTermsJSON),
    });
  }

  function MerchantRowActions({ row }: { row: MerchantIcon }) {
    return (
      <div className="row-actions">
        {isVectorAssetURL(row.iconURL) && (
          <button
            className="btn btn-brass btn-sm"
            type="button"
            disabled={busy || importBusySlug === row.sourceSlug}
            onClick={() => void convertToPng(row)}
          >
            {importBusySlug === row.sourceSlug ? "Converting…" : "Convert to PNG"}
          </button>
        )}
        {row.sourceName === "simple-icons" && (
          <button
            className="btn btn-brass btn-sm"
            type="button"
            disabled={busy || importBusySlug === row.sourceSlug}
            onClick={() => void redownload(row)}
          >
            {importBusySlug === row.sourceSlug ? "Downloading…" : "Redownload"}
          </button>
        )}
        <button className="btn btn-secondary btn-sm" type="button" onClick={() => startEdit(row)}>
          Edit
        </button>
        {row.isActive !== false && (
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            disabled={busy}
            onClick={() => void onDeactivate(row)}
          >
            Deactivate
          </button>
        )}
        <button
          className="btn btn-danger btn-sm"
          type="button"
          disabled={busy}
          onClick={() => void onDelete(row)}
        >
          Delete
        </button>
      </div>
    );
  }

  function MerchantBadges({ row }: { row: MerchantIcon }) {
    return (
      <div className="row-actions" style={{ flexWrap: "wrap" }}>
        {row.iconURL ? <span className="badge ok">Icon</span> : <span className="badge warn">No icon</span>}
        {isVectorAssetURL(row.iconURL) && (
          <span className="badge warn" title="The app cannot decode SVG. Convert this icon to publish a PNG.">
            SVG · blank in app
          </span>
        )}
        {!row.isActive && <span className="badge">Inactive</span>}
        {row.mergedIntoRecordName && <span className="badge warn">Merged</span>}
        <span className="badge">{walletCategoryLabel(row.walletCategory)}</span>
        {row.granularKind && <span className="badge">{row.granularKind}</span>}
      </div>
    );
  }

  return (
    <AppShell>
      <main className="page merchants-page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Merchants</h1>
            <p>
              Directory of brands for on-device matching — Wallet category, OSM kind, aliases, and
              optional R2 icons. Icons are not required for every chain.
            </p>
          </div>
          <div className="row-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() =>
                document.getElementById("import-brands")?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Icon name="upload" size={15} />
              Import
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy || catalogLoading}
              onClick={() =>
                void ensureCatalogLoaded().then(() => {
                  document.getElementById("simple-icons-catalog")?.scrollIntoView({ behavior: "smooth" });
                })
              }
            >
              <Icon name="bag" size={15} />
              Simple Icons
            </button>
            <button className="btn btn-primary" type="button" onClick={() => setEditing(defaultDraft())}>
              <Icon name="plus" size={15} />
              Add merchant
            </button>
          </div>
        </div>

        {error && <div className="banner error">{error}</div>}
        {message && <div className="banner success">{message}</div>}

        <section className="merchant-stats">
          <button type="button" className="merchant-stat" onClick={() => setIconFilter("all")}>
            <span className="muted">Total</span>
            <strong>{rows.length}</strong>
          </button>
          <button type="button" className="merchant-stat" onClick={() => setIconFilter("hasIcon")}>
            <span className="muted">With icon</span>
            <strong>{withIconCount}</strong>
          </button>
          <button
            type="button"
            className={`merchant-stat${iconFilter === "missingIcon" ? " is-active" : ""}`}
            onClick={() => setIconFilter(iconFilter === "missingIcon" ? "all" : "missingIcon")}
          >
            <span className="muted">Missing icon</span>
            <strong>{missingIconCount}</strong>
          </button>
          <button
            type="button"
            className="merchant-stat"
            onClick={() => setReviewFilter(reviewFilter === "needsReview" ? "all" : "needsReview")}
          >
            <span className="muted">Showing</span>
            <strong>
              {filtered.length}
              <span className="muted" style={{ fontWeight: 500, fontSize: "0.85rem" }}>
                {" "}
                of {rows.length}
              </span>
            </strong>
          </button>
        </section>

        {editing && (
          <form className="panel form-grid" onSubmit={(e) => void onSubmit(e)}>
            <div className="full panel-head" style={{ marginBottom: 0, paddingBottom: "0.35rem" }}>
              <div>
                <span className="overline">Editor</span>
                <h2>{editing.recordName ? "Edit merchant" : "New merchant"}</h2>
              </div>
            </div>
            <label>
              Display name
              <input
                value={editing.displayName ?? ""}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                required
              />
            </label>
            <label>
              Normalized name
              <input
                value={editing.normalizedName ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, normalizedName: e.target.value.toLowerCase() })
                }
                required
              />
            </label>
            <label>
              Slug
              <input
                value={editing.sourceSlug ?? ""}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    sourceSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                  })
                }
                required
                disabled={Boolean(editing.recordName)}
              />
            </label>
            <label>
              Brand hex
              <input
                value={editing.brandHex ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, brandHex: e.target.value.replace(/^#/, "") })
                }
                placeholder="E50914"
              />
            </label>
            <label className="full">
              Match aliases (comma-separated)
              <input
                value={editing.matchTermsText ?? ""}
                onChange={(e) => setEditing({ ...editing, matchTermsText: e.target.value })}
                placeholder="netflix.com, nflx"
              />
            </label>
            <label>
              Wallet category
              <select
                value={editing.walletCategory ?? walletCategoryFromHint(editing.categoryHint)}
                onChange={(e) => {
                  const walletCategory = e.target.value as WalletCategory;
                  setEditing({
                    ...editing,
                    walletCategory,
                    categoryHint: walletCategoryLabel(walletCategory),
                    granularKind:
                      editing.granularKind && !editing.granularKind.startsWith("cms/")
                        ? editing.granularKind
                        : `cms/${walletCategory}`,
                  });
                }}
              >
                {WALLET_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {walletCategoryLabel(category)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Granular kind
              <input
                list="granular-kind-tags"
                value={editing.granularKind ?? ""}
                onChange={(e) => setEditing({ ...editing, granularKind: e.target.value })}
                placeholder="amenity/fast_food"
              />
              <datalist id="granular-kind-tags">
                {OSM_TAG_MAPPINGS.map((row) => (
                  <option key={row.osm} value={row.osm} />
                ))}
              </datalist>
            </label>
            <label>
              Source
              <select
                value={editing.sourceKind ?? "cms"}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    sourceKind: e.target.value as MerchantSourceKind,
                    sourceName: e.target.value,
                  })
                }
              >
                {MERCHANT_SOURCE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Wikidata
              <input
                value={editing.wikidataId ?? ""}
                onChange={(e) => setEditing({ ...editing, wikidataId: e.target.value || null })}
                placeholder="Q37614"
              />
            </label>
            <label>
              NSI id
              <input
                value={editing.nsiId ?? ""}
                onChange={(e) => setEditing({ ...editing, nsiId: e.target.value || null })}
                placeholder="jollibee-9b2018"
              />
            </label>
            <label className="full">
              Countries
              <div className="row-actions" style={{ marginTop: "0.35rem" }}>
                {(["ph", "001"] as const).map((code) => {
                  const current = parseCountryScopeJSON(editing.countryScopeJSON);
                  const checked = current.includes(code);
                  return (
                    <label key={code} className="checkbox" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? current.filter((item) => item !== code)
                            : [...current, code];
                          setEditing({ ...editing, countryScopeJSON: encodeCountryScope(next) });
                        }}
                      />
                      {code === "ph" ? "PH" : "Global (001)"}
                    </label>
                  );
                })}
              </div>
            </label>
            <label>
              Sort order
              <input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
              />
            </label>
            <label className="full">
              Icon URL (R2)
              <input
                value={editing.iconURL ?? ""}
                onChange={(e) => setEditing({ ...editing, iconURL: e.target.value || null })}
                placeholder="https://images.pambayad.com/merchant/…"
              />
            </label>
            <label className="full">
              Replace PNG
              <input
                type="file"
                accept="image/png,image/webp,image/jpeg,image/svg+xml"
                onChange={(e) => void onUploadReplacement(e.target.files?.[0] ?? null)}
              />
            </label>
            <label className="checkbox full">
              <input
                type="checkbox"
                checked={uploadAsIs}
                onChange={(e) => setUploadAsIs(e.target.checked)}
              />
              Upload as-is (resize only)
            </label>
            <p className="muted full" style={{ margin: 0, fontSize: "0.85rem" }}>
              Skips white knockout and recolor when checked. Otherwise PNGs that are already
              white-on-transparent are detected automatically and left unchanged.
            </p>
            {editing.iconURL && (
              <div className="full" style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                <div className="merchant-card-art" style={{ width: 48, height: 48, borderRadius: 12 }}>
                  <img src={editing.iconURL} alt="" />
                </div>
                <span className="badge ok">R2 cached</span>
              </div>
            )}
            <label className="checkbox">
              <input
                type="checkbox"
                checked={editing.isActive !== false}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              Active
            </label>
            {editing.mergedIntoRecordName && (
              <p className="muted full" style={{ margin: 0 }}>
                Merged into <code>{editing.mergedIntoRecordName}</code>
              </p>
            )}
            {editing.recordName &&
              rows.some((row) => row.mergedIntoRecordName === editing.recordName) && (
                <p className="muted full" style={{ margin: 0 }}>
                  Merge aliases:{" "}
                  {rows
                    .filter((row) => row.mergedIntoRecordName === editing.recordName)
                    .map((row) => row.displayName)
                    .join(", ")}
                </p>
              )}
            <div className="row-actions full">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Save
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setEditing(null)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <section className="panel merchants-directory">
          <div className="panel-head">
            <div>
              <span className="overline">Directory</span>
              <h2>
                {filtered.length} merchant{filtered.length === 1 ? "" : "s"}
              </h2>
            </div>
            <div className="view-toggle" role="group" aria-label="View mode">
              <button
                type="button"
                className={`view-toggle-btn${viewMode === "gallery" ? " is-active" : ""}`}
                onClick={() => setViewMode("gallery")}
                aria-pressed={viewMode === "gallery"}
              >
                <Icon name="grid" size={15} />
                Gallery
              </button>
              <button
                type="button"
                className={`view-toggle-btn${viewMode === "list" ? " is-active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
              >
                <Icon name="list" size={15} />
                List
              </button>
            </div>
          </div>

          <div className="merchants-toolbar">
            <div className="field search merchants-search">
              <input
                type="search"
                placeholder="Search name, slug, Wikidata, NSI…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="merchants-filters">
              <label className="field">
                Wallet
                <select
                  value={walletFilter}
                  onChange={(e) => setWalletFilter(e.target.value as WalletCategory | "all")}
                >
                  <option value="all">All</option>
                  {WALLET_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {walletCategoryLabel(category)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Kind
                <input
                  list="granular-kind-filter"
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                  placeholder="amenity/fast_food"
                />
                <datalist id="granular-kind-filter">
                  {OSM_TAG_MAPPINGS.map((row) => (
                    <option key={row.osm} value={row.osm} />
                  ))}
                </datalist>
              </label>
              <label className="field">
                Scope
                <select
                  value={scopeFilter}
                  onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
                >
                  <option value="all">All</option>
                  <option value="ph">PH</option>
                  <option value="global">Global / 001</option>
                </select>
              </label>
              <label className="field">
                Source
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value as MerchantSourceKind | "all")}
                >
                  <option value="all">All</option>
                  {MERCHANT_SOURCE_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kind}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Icon
                <select
                  value={iconFilter}
                  onChange={(e) => setIconFilter(e.target.value as IconFilter)}
                >
                  <option value="all">All</option>
                  <option value="hasIcon">Has icon</option>
                  <option value="missingIcon">Missing icon</option>
                </select>
              </label>
              <label className="field">
                Review
                <select
                  value={reviewFilter}
                  onChange={(e) => setReviewFilter(e.target.value as ReviewFilter)}
                >
                  <option value="all">All</option>
                  <option value="needsReview">Needs review</option>
                  <option value="merged">Merged aliases</option>
                </select>
              </label>
            </div>
          </div>

          <div className="merchants-tools">
            <button
              className="btn btn-quiet btn-sm"
              type="button"
              disabled={busy || !rows.some((row) => isVectorAssetURL(row.iconURL))}
              onClick={() => void convertAllToPng()}
            >
              Convert all SVGs
            </button>
            <button
              className="btn btn-quiet btn-sm"
              type="button"
              disabled={busy || rows.length === 0}
              onClick={() => void redownloadAll()}
            >
              Redownload Simple Icons
            </button>
            <button
              className="btn btn-quiet btn-sm"
              type="button"
              disabled={busy}
              onClick={() => void importMissing()}
            >
              Import missing curated
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-glyph">
                <Icon name="bag" size={28} />
              </div>
              <strong>No merchants match</strong>
              Clear filters, import brands, or add one manually.
            </div>
          ) : viewMode === "gallery" ? (
            <div className="merchant-grid">
              {filtered.map((row) => (
                <article key={row.recordName} className="merchant-card">
                  <div className="merchant-card-art">
                    {row.iconURL ? (
                      <img src={row.iconURL} alt="" />
                    ) : (
                      <span className="placeholder">{row.displayName.slice(0, 1)}</span>
                    )}
                  </div>
                  <div className="merchant-card-body">
                    <strong>{row.displayName}</strong>
                    <span className="muted">{row.normalizedName}</span>
                    <div style={{ marginTop: "0.5rem" }}>
                      <MerchantBadges row={row} />
                    </div>
                    <div style={{ marginTop: "0.65rem" }}>
                      <MerchantRowActions row={row} />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Icon</th>
                    <th>Name</th>
                    <th>Wallet</th>
                    <th>Kind</th>
                    <th>Scope</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.recordName}>
                      <td>
                        {row.iconURL ? (
                          <div className="merchant-card-art" style={{ width: 40, height: 40 }}>
                            <img src={row.iconURL} alt="" />
                          </div>
                        ) : (
                          <span className="badge warn">Missing</span>
                        )}
                      </td>
                      <td>
                        <strong>{row.displayName}</strong>
                        <div className="muted" style={{ fontSize: "0.78rem" }}>
                          {row.normalizedName}
                        </div>
                      </td>
                      <td>{walletCategoryLabel(row.walletCategory)}</td>
                      <td>
                        <code style={{ fontSize: "0.78rem" }}>{row.granularKind || "—"}</code>
                      </td>
                      <td>{countriesLabel(row)}</td>
                      <td>{row.sourceKind}</td>
                      <td>
                        <div className="row-actions" style={{ flexWrap: "wrap" }}>
                          {!row.isActive && <span className="badge">Inactive</span>}
                          {row.mergedIntoRecordName && <span className="badge warn">Merged</span>}
                          {row.isActive && !row.mergedIntoRecordName && (
                            <span className="badge ok">Active</span>
                          )}
                          {isVectorAssetURL(row.iconURL) && <span className="badge warn">SVG</span>}
                        </div>
                      </td>
                      <td>
                        <MerchantRowActions row={row} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <MerchantBrandImportPanel
          rows={rows}
          institutions={institutions}
          busy={busy}
          onBusy={setBusy}
          onError={setError}
          onMessage={setMessage}
          onReloaded={reload}
        />

        <section className="panel banner info" style={{ margin: 0 }}>
          <Icon name="alert" size={16} />
          <div>
            <strong>Source &amp; trademarks.</strong> SVG paths are CC0 via Simple Icons; brand marks
            remain their owners’. Use only for merchant identification — never endorsement. See{" "}
            <code>cms/catalog/SOURCES.md</code>.
          </div>
        </section>

        <section className="panel" id="simple-icons-catalog">
          <div className="panel-head">
            <div>
              <span className="overline">Art · Simple Icons {SIMPLE_ICONS_CDN_VERSION}</span>
              <h2>Icon catalog</h2>
            </div>
            <div className="row-actions">
              {!catalog ? (
                <button
                  className="btn btn-brass"
                  type="button"
                  disabled={catalogLoading || busy}
                  onClick={() => void ensureCatalogLoaded()}
                >
                  {catalogLoading ? "Loading…" : "Load catalog"}
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    disabled={catalogLoading || busy}
                    onClick={() => void ensureCatalogLoaded(true)}
                  >
                    Refresh list
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    disabled={busy || catalogMatches.every((e) => cachedSlugs.has(e.slug))}
                    onClick={selectVisibleCatalog}
                  >
                    Select visible
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    disabled={selectedSlugs.size === 0}
                    onClick={clearCatalogSelection}
                  >
                    Clear selection
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    disabled={busy || selectedNotCached.length === 0}
                    onClick={() => void importCatalogSelection()}
                  >
                    Import selected ({selectedNotCached.length})
                  </button>
                </>
              )}
            </div>
          </div>

          <p className="muted" style={{ marginTop: 0 }}>
            Upsert icon art onto matching merchant rows. Rasterized to PNG and cached on R2 —
            devices never call Simple Icons.
          </p>

          {catalogError && <div className="banner error">{catalogError}</div>}

          {catalog && (
            <>
              <div className="row-actions" style={{ marginBottom: "0.85rem", flexWrap: "wrap" }}>
                <div className="field search" style={{ maxWidth: 320, flex: "1 1 220px" }}>
                  <input
                    type="search"
                    placeholder="Search title, slug, or alias…"
                    value={catalogQuery}
                    onChange={(e) => setCatalogQuery(e.target.value)}
                  />
                </div>
                <label className="checkbox" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={showCachedInCatalog}
                    onChange={(e) => setShowCachedInCatalog(e.target.checked)}
                  />
                  Show already cached
                </label>
                <span className="muted">
                  {catalog.length.toLocaleString()} brands · showing {catalogMatches.length}
                  {selectedSlugs.size > 0 ? ` · ${selectedSlugs.size} selected` : ""}
                </span>
              </div>

              {catalogMatches.length === 0 ? (
                <div className="empty">
                  <strong>No matches</strong>
                  Try another search, or turn on “Show already cached”.
                </div>
              ) : (
                <div className="merchant-grid catalog-pick-grid">
                  {catalogMatches.map((entry) => {
                    const cached = cachedSlugs.has(entry.slug);
                    const selected = selectedSlugs.has(entry.slug);
                    const loading = importBusySlug === entry.slug;
                    return (
                      <article
                        key={entry.slug}
                        className={`merchant-card catalog-pick-card${selected ? " is-selected" : ""}${cached ? " is-cached" : ""}`}
                      >
                        <label className="catalog-pick-label">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={cached || busy}
                            onChange={() => toggleCatalogSlug(entry.slug)}
                          />
                          <div
                            className="merchant-card-art"
                            style={{ background: `#${entry.hex}22` }}
                          >
                            <img
                              src={simpleIconsPreviewUrl(entry.slug, entry.hex)}
                              alt=""
                              loading="lazy"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                          <div className="merchant-card-body">
                            <strong>{entry.title}</strong>
                            <span className="muted">{entry.slug}</span>
                            <div className="row-actions" style={{ marginTop: "0.5rem" }}>
                              {cached ? (
                                <span className="badge ok">Cached</span>
                              ) : selected ? (
                                <span className="badge ok">Selected</span>
                              ) : (
                                <span className="badge">Available</span>
                              )}
                              <span className="badge" title={`#${entry.hex}`}>
                                #{entry.hex}
                              </span>
                            </div>
                          </div>
                        </label>
                        {!cached && (
                          <div className="row-actions" style={{ padding: "0 0.85rem 0.85rem" }}>
                            <button
                              className="btn btn-brass btn-sm"
                              type="button"
                              disabled={busy || loading}
                              onClick={() => void importCurated(catalogEntryToImportDraft(entry))}
                            >
                              {loading ? "Importing…" : "Import now"}
                            </button>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <span className="overline">Shortcuts</span>
              <h2>Curated presets</h2>
            </div>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            Common subscription brands with ready-made match aliases. Prefer the icon catalog above
            when you need anything outside this short list.
          </p>
          <div className="merchant-grid">
            {CURATED_MERCHANT_ICONS.map((item) => {
              const cached = cachedSlugs.has(item.sourceSlug);
              const loading = importBusySlug === item.sourceSlug;
              return (
                <article key={item.sourceSlug} className="merchant-card">
                  <div className="merchant-card-art">
                    <span className="placeholder">{item.displayName.slice(0, 1)}</span>
                  </div>
                  <div className="merchant-card-body">
                    <strong>{item.displayName}</strong>
                    <span className="muted">{item.sourceSlug}</span>
                    <div className="row-actions" style={{ marginTop: "0.65rem" }}>
                      {cached ? (
                        <span className="badge ok">Cached</span>
                      ) : (
                        <button
                          className="btn btn-brass btn-sm"
                          type="button"
                          disabled={busy || loading}
                          onClick={() => void importCurated(item)}
                        >
                          {loading ? "Importing…" : "Import"}
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </main>
    </AppShell>
  );
}
